/**
 * useMapState.js
 * Custom hook to manage map state: buildings, statuses, selections
 */

import { useState, useCallback } from 'react';

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const useMapState = () => {
  const [buildings, setBuildings] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  const [hoverBuildingId, setHoverBuildingId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Initialize buildings from API response
   */
  const initializeBuildings = useCallback((buildingsData) => {
    try {
      // Transform API response to internal format
      const transformed = buildingsData.map((building) => ({
        id: building.host,
        name: building.host,
        lat: parseFloat(building.lat),
        lng: parseFloat(building.long),
        cellhubCount: building.cellhub_count,
        cellhubOnline: building.cellhub_online,
        userCount: building.user_count,
        buildingAlarm: toNumberOrNull(building.building_alarm),
        tempAlarm: toNumberOrNull(building.temp_alarm),
        serviceAlarm: toNumberOrNull(building.service_alarm),
        powerAlarm: toNumberOrNull(building.power_alarm),
        oamAlarm: toNumberOrNull(building.oam_alarm),
        availability: building.cellhub_availability,

        // Detailed user and throughput metrics by carrier/total.
        attCurUsers: toNumberOrNull(building.att_curusers),
        attCurRx: toNumberOrNull(building.att_currx),
        attCurTx: toNumberOrNull(building.att_curtx),
        attHistUsers: toNumberOrNull(building.att_histusers),
        attHistRx: toNumberOrNull(building.att_histrx),
        attHistTx: toNumberOrNull(building.att_histtx),

        tmoCurUsers: toNumberOrNull(building.tmo_curusers),
        tmoCurRx: toNumberOrNull(building.tmo_currx),
        tmoCurTx: toNumberOrNull(building.tmo_curtx),
        tmoHistUsers: toNumberOrNull(building.tmo_histusers),
        tmoHistRx: toNumberOrNull(building.tmo_histrx),
        tmoHistTx: toNumberOrNull(building.tmo_histtx),

        totalCurUsers: toNumberOrNull(building.cur_users),
        totalCurRx: toNumberOrNull(building.cur_rx),
        totalCurTx: toNumberOrNull(building.cur_tx),
        totalHistUsers: toNumberOrNull(building.hist_users),
        totalHistRx: toNumberOrNull(building.hist_rx),
        totalHistTx: toNumberOrNull(building.hist_tx)
      }));

      setBuildings(transformed);
      setError(null);

      // Initialize status map
      const statusMap = {};
      transformed.forEach((b) => {
        statusMap[b.id] = {
          availability: b.availability,
          userCount: b.userCount,
          buildingAlarm: b.buildingAlarm,
          tempAlarm: b.tempAlarm,
          serviceAlarm: b.serviceAlarm,
          powerAlarm: b.powerAlarm,
          oamAlarm: b.oamAlarm,

          attCurUsers: b.attCurUsers,
          attCurRx: b.attCurRx,
          attCurTx: b.attCurTx,
          attHistUsers: b.attHistUsers,
          attHistRx: b.attHistRx,
          attHistTx: b.attHistTx,

          tmoCurUsers: b.tmoCurUsers,
          tmoCurRx: b.tmoCurRx,
          tmoCurTx: b.tmoCurTx,
          tmoHistUsers: b.tmoHistUsers,
          tmoHistRx: b.tmoHistRx,
          tmoHistTx: b.tmoHistTx,

          totalCurUsers: b.totalCurUsers,
          totalCurRx: b.totalCurRx,
          totalCurTx: b.totalCurTx,
          totalHistUsers: b.totalHistUsers,
          totalHistRx: b.totalHistRx,
          totalHistTx: b.totalHistTx
        };
      });
      setStatuses(statusMap);

      return transformed;
    } catch (err) {
      setError('Failed to initialize buildings');
      console.error('Error initializing buildings:', err);
      return [];
    }
  }, []);

  /**
   * Update statuses for all buildings
   */
  const updateStatuses = useCallback((newStatusData) => {
    try {
      const statusMap = {};
      newStatusData.forEach((building) => {
        statusMap[building.host] = {
          availability: building.cellhub_availability,
          userCount: building.user_count,
          buildingAlarm: toNumberOrNull(building.building_alarm),
          tempAlarm: toNumberOrNull(building.temp_alarm),
          serviceAlarm: toNumberOrNull(building.service_alarm),
          powerAlarm: toNumberOrNull(building.power_alarm),
          oamAlarm: toNumberOrNull(building.oam_alarm),
          attCurUsers: toNumberOrNull(building.att_curusers),
          attCurRx: toNumberOrNull(building.att_currx),
          attCurTx: toNumberOrNull(building.att_curtx),
          attHistUsers: toNumberOrNull(building.att_histusers),
          attHistRx: toNumberOrNull(building.att_histrx),
          attHistTx: toNumberOrNull(building.att_histtx),
          tmoCurUsers: toNumberOrNull(building.tmo_curusers),
          tmoCurRx: toNumberOrNull(building.tmo_currx),
          tmoCurTx: toNumberOrNull(building.tmo_curtx),
          tmoHistUsers: toNumberOrNull(building.tmo_histusers),
          tmoHistRx: toNumberOrNull(building.tmo_histrx),
          tmoHistTx: toNumberOrNull(building.tmo_histtx),
          totalCurUsers: toNumberOrNull(building.cur_users),
          totalCurRx: toNumberOrNull(building.cur_rx),
          totalCurTx: toNumberOrNull(building.cur_tx),
          totalHistUsers: toNumberOrNull(building.hist_users),
          totalHistRx: toNumberOrNull(building.hist_rx),
          totalHistTx: toNumberOrNull(building.hist_tx),
          timestamp: new Date().toISOString()
        };
      });

      setStatuses(statusMap);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError('Failed to update statuses');
      console.error('Error updating statuses:', err);
    }
  }, []);

  /**
   * Select a building marker
   */
  const selectBuilding = useCallback((buildingId) => {
    setSelectedBuildingId(buildingId);
  }, []);

  /**
   * Deselect current building
   */
  const deselectBuilding = useCallback(() => {
    setSelectedBuildingId(null);
  }, []);

  /**
   * Hover over a building marker
   */
  const hoverBuilding = useCallback((buildingId) => {
    setHoverBuildingId(buildingId);
  }, []);

  /**
   * Remove hover
   */
  const clearHover = useCallback(() => {
    setHoverBuildingId(null);
  }, []);

  /**
   * Get building data by ID
   */
  const getBuilding = useCallback(
    (buildingId) => {
      return buildings.find((b) => b.id === buildingId);
    },
    [buildings]
  );

  /**
   * Get status for a building
   */
  const getStatus = useCallback(
    (buildingId) => {
      return statuses[buildingId] || null;
    },
    [statuses]
  );

  /**
   * Get selected building full data
   */
  const getSelectedBuilding = useCallback(() => {
    if (!selectedBuildingId) return null;
    return getBuilding(selectedBuildingId);
  }, [selectedBuildingId, getBuilding]);

  /**
   * Set loading state
   */
  const setLoadingState = useCallback((loading) => {
    setIsLoading(loading);
  }, []);

  /**
   * Set error message
   */
  const setErrorMessage = useCallback((message) => {
    setError(message);
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    buildings,
    statuses,
    selectedBuildingId,
    hoverBuildingId,
    lastUpdated,
    isLoading,
    error,

    // Initialization
    initializeBuildings,

    // Updates
    updateStatuses,

    // Selection
    selectBuilding,
    deselectBuilding,
    getSelectedBuilding,

    // Hover
    hoverBuilding,
    clearHover,

    // Getters
    getBuilding,
    getStatus,

    // Loading/Error
    setLoadingState,
    setErrorMessage,
    clearError
  };
};
